import { Server, Cpu, Globe, ShieldAlert } from 'lucide-react';
import './KpiCards.css';

/* ─── Barre segmentée colorée ──────────────────────────────── */

function SegBar({ segments }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) {
    return <div className="kpi-bar"><div className="kpi-bar-seg" style={{ width: '100%', background: '#e5e7eb' }} /></div>;
  }

  return (
    <div className="kpi-bar">
      {segments.map((seg, i) => {
        if (seg.value === 0) return null;
        return (
          <div
            key={i}
            className="kpi-bar-seg"
            style={{
              width:      `${(seg.value / total) * 100}%`,
              background: seg.color,
            }}
          />
        );
      })}
    </div>
  );
}

/* ─── Texte de détail ──────────────────────────────────────── */

function Breakdown({ items }) {
  return (
    <div className="kpi-breakdown">
      {items.filter(i => i.value > 0).map((item, i) => (
        <span key={i} className="kpi-stat">
          <span className="kpi-stat-dot" style={{ background: item.color }} />
          {item.value} {item.label}
        </span>
      ))}
    </div>
  );
}

/* ─── Card générique ────────────────────────────────────────── */

function KpiCard({ label, value, accentColor, iconBg, icon, segments, breakdown }) {
  return (
    <div className="kpi-card">
      <div className="kpi-accent-bar" style={{ background: accentColor }} />

      <div className="kpi-top">
        <div>
          <div className="kpi-label">{label}</div>
          <div className="kpi-value">{value}</div>
        </div>
        <div className="kpi-icon" style={{ background: iconBg, color: accentColor }}>
          {icon}
        </div>
      </div>

      {segments && <SegBar segments={segments} />}
      {breakdown && <Breakdown items={breakdown} />}
    </div>
  );
}

/* ─── Card alertes (pas de barre) ───────────────────────────── */

function AlertCard({ count }) {
  const color  = count > 0 ? '#dc2626' : '#16a34a';
  const icon   = count > 0 ? '#dc2626' : '#16a34a';
  const iconBg = count > 0 ? '#fee2e2' : '#dcfce7';
  const label = count > 0 ? 'Intervention requise' : 'Tout est nominal';

  return (
    <div className="kpi-card">
      <div className="kpi-accent-bar" style={{ background: color }} />

      <div className="kpi-top">
        <div>
          <div className="kpi-label">Alertes critiques</div>
          <div className="kpi-alert-value" style={{ color }}>{count}</div>
          <div className="kpi-alert-sub" style={{ color }}>{label}</div>
        </div>
        <div className="kpi-icon" style={{ background: iconBg, color: icon }}>
          <ShieldAlert size={18} />
        </div>
      </div>
    </div>
  );
}

/* ─── Stats helpers ─────────────────────────────────────────── */

function getServerStats(servers) {
  if (!servers?.success) return { total: 0, ok: 0, degrade: 0, crit: 0 };
  let ok = 0, degrade = 0, crit = 0;
  for (const s of servers.data) {
    const st = (s.server_status || '').toLowerCase();
    if (st === 'hors ligne') crit++;
    else if (st === 'dégradé') degrade++;
    else ok++;
  }
  return { total: servers.data.length, ok, degrade, crit };
}

function getServiceStats(services) {
  if (!services?.success) return { total: 0, ok: 0, crit: 0 };
  let ok = 0, crit = 0;
  for (const s of services.data) {
    if (s.service_status === 'Opérationnel') ok++; else crit++;
  }
  return { total: services.data.length, ok, crit };
}

function getWebsiteStats(websites) {
  if (!websites?.success || !websites?.data) return { total: 0, ok: 0, slow: 0, crit: 0 };
  const list  = websites.data.websites || [];
  const total = websites.data.pagination?.total || list.length;
  let ok = 0, slow = 0, crit = 0;
  for (const site of list) {
    const s = (site.website_status || '').toLowerCase();
    if (s === 'inaccessible' || s === 'hors ligne') crit++;
    else if (s === 'ralenti' || s === 'dégradé') slow++;
    else ok++;
  }
  return { total, ok, slow, crit };
}

function getCritCount(servers, services) {
  let n = 0;
  if (servers?.success) {
    for (const s of servers.data) {
      if ((s.server_status || '').toLowerCase() === 'hors ligne') n++;
      for (const m of (s.metrics || [])) { if (parseInt(m.alerte, 10) === 4) n++; }
    }
  }
  if (services?.success) {
    for (const s of services.data) { if (s.service_status === 'Hors ligne') n++; }
  }
  return n;
}

/* ─── Export ────────────────────────────────────────────────── */

function KpiCards({ servers, services, websites }) {
  const sv = getServerStats(servers);
  const sc = getServiceStats(services);
  const wb = getWebsiteStats(websites);
  const cr = getCritCount(servers, services);

  return (
    <div className="kpi-grid">

      <KpiCard
        label="Serveurs"
        value={sv.total}
        accentColor="#7c3aed"
        iconBg="rgba(124,58,237,0.08)"
        icon={<Server size={18} />}
        segments={[
          { value: sv.ok,      color: '#16a34a' },
          { value: sv.degrade, color: '#ea580c' },
          { value: sv.crit,    color: '#dc2626' },
        ]}
        breakdown={[
          { value: sv.ok,      color: '#16a34a', label: 'En ligne'   },
          { value: sv.degrade, color: '#ea580c', label: 'Dégradés'   },
          { value: sv.crit,    color: '#dc2626', label: 'Hors ligne' },
        ]}
      />

      <KpiCard
        label="Services"
        value={sc.total}
        accentColor="#0891b2"
        iconBg="rgba(8,145,178,0.08)"
        icon={<Cpu size={18} />}
        segments={[
          { value: sc.ok,   color: '#16a34a' },
          { value: sc.crit, color: '#dc2626' },
        ]}
        breakdown={[
          { value: sc.ok,   color: '#16a34a', label: 'Opérationnels' },
          { value: sc.crit, color: '#dc2626', label: 'Hors ligne'    },
        ]}
      />

      <KpiCard
        label="Sites clients"
        value={wb.total}
        accentColor="#16a34a"
        iconBg="rgba(22,163,74,0.08)"
        icon={<Globe size={18} />}
        segments={[
          { value: wb.ok,   color: '#16a34a' },
          { value: wb.slow, color: '#d97706' },
          { value: wb.crit, color: '#dc2626' },
        ]}
        breakdown={[
          { value: wb.ok,   color: '#16a34a', label: 'En ligne'      },
          { value: wb.slow, color: '#d97706', label: 'Ralentis'      },
          { value: wb.crit, color: '#dc2626', label: 'Inaccessibles' },
        ]}
      />

      <AlertCard count={cr} />

    </div>
  );
}

export default KpiCards;
